import { useState, useRef } from "react";

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "market" | "territory";
}

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

export default function FormModal({ isOpen, onClose, type }: FormModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [stateError, setStateError] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const config = {
    market: {
      title: "Check Market Availability",
      subtitle: "Tell us where you operate and we'll confirm availability in your area.",
      submitUrl: "https://readdy.ai/api/form/d70lknj2m2156g4s6ur0",
      formId: "market-availability-form",
    },
    territory: {
      title: "Apply for Territory Access",
      subtitle: "Apply to secure exclusive or shared territory access in your market.",
      submitUrl: "https://readdy.ai/api/form/d70lknj2m2156g4s6urg",
      formId: "territory-access-form",
    },
  }[type];

  const toggleState = (abbr: string) => {
    setStateError(false);
    setSelectedStates((prev) =>
      prev.includes(abbr) ? prev.filter((s) => s !== abbr) : [...prev, abbr]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedStates.length === 0) {
      setStateError(true);
      return;
    }
    if (!formRef.current) return;
    setLoading(true);
    try {
      const data = new URLSearchParams(
        new FormData(formRef.current) as unknown as Record<string, string>
      );
      data.set("states_of_interest", selectedStates.join(", "));
      await fetch(config.submitUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setSelectedStates([]);
    setStateError(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:px-4 sm:py-6"
      style={{ backgroundColor: "rgba(11,31,51,0.7)" }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md relative flex flex-col max-h-[85vh] sm:max-h-[90vh]">

        {/* Fixed header */}
        <div className="px-5 sm:px-8 pt-5 sm:pt-8 pb-0 flex-shrink-0">
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 w-11 h-11 flex items-center justify-center text-gray-400 hover:text-navy cursor-pointer rounded-xl hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {submitted ? (
          <div className="text-center py-12 px-5 sm:px-8">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-green-50 rounded-full">
              <i className="ri-check-double-line text-3xl text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-navy mb-2">You&apos;re on the list!</h3>
            <p className="text-[#4a5568] text-sm">
              We&apos;ll review your market and follow up within 1 business day.
            </p>
            <button
              onClick={handleClose}
              className="mt-6 bg-primary text-white font-semibold px-6 py-2.5 rounded-lg cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 px-5 sm:px-8 pt-6 pb-4">
              <h3 className="text-xl sm:text-2xl font-bold text-navy mb-1 pr-8">{config.title}</h3>
              <p className="text-[#4a5568] text-sm mb-6">{config.subtitle}</p>

              <form
                id={config.formId}
                ref={formRef}
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-4"
              >
                {/* Name row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-navy mb-1 block">First Name</label>
                    <input
                      name="first_name"
                      type="text"
                      required
                      placeholder="Jane"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-navy mb-1 block">Last Name</label>
                    <input
                      name="last_name"
                      type="text"
                      required
                      placeholder="Smith"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="text-xs font-semibold text-navy mb-1 block">Email Address</label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="jane@titlecompany.com"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-primary"
                  />
                </div>

                {/* Company */}
                <div>
                  <label className="text-xs font-semibold text-navy mb-1 block">Company Name</label>
                  <input
                    name="company"
                    type="text"
                    required
                    placeholder="Premier Title Group"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-primary"
                  />
                </div>

                {/* Phone + Role */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-navy mb-1 block">Phone</label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      placeholder="(555) 000-0000"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-navy mb-1 block">Role</label>
                    <input
                      name="role"
                      type="text"
                      required
                      placeholder="e.g. Escrow Officer"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                {/* Outreach Strategy Interest */}
                <div>
                  <label className="text-xs font-semibold text-navy mb-1 block">Outreach Strategy Interest</label>
                  <select
                    name="outreach_strategy_interest"
                    required
                    defaultValue=""
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-primary bg-white cursor-pointer"
                  >
                    <option value="" disabled>Select your strategy...</option>
                    <option value="Tier 1: Outbound Engine (Your Data)">Tier 1: Outbound Engine — Your Data</option>
                    <option value="Tier 2: Market-Triggered Outreach">Tier 2: Market-Triggered Outreach (Most Popular)</option>
                    <option value="Tier 3: Market Expansion">Tier 3: Market Expansion</option>
                    <option value="Not Sure">Not Sure</option>
                  </select>
                </div>

                {/* Markets / Counties of Interest */}
                <div>
                  <label className="text-xs font-semibold text-navy mb-1 block">Markets / Counties of Interest</label>
                  <input
                    name="markets_counties"
                    type="text"
                    placeholder="e.g. Travis County TX, Miami-Dade FL, Cook County IL"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:outline-none focus:border-primary"
                  />
                  <p className="text-xs text-gray-400 mt-1">List any specific counties or metro areas you want to target.</p>
                </div>

                {/* States of Interest */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-navy block">
                      States of Interest
                    </label>
                    {selectedStates.length > 0 && (
                      <span className="text-xs font-semibold text-primary">
                        {selectedStates.length} selected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#4a5568] mb-3">
                    Select every state where you operate or want to expand.
                  </p>

                  <div className="grid grid-cols-5 sm:grid-cols-6 gap-1.5">
                    {US_STATES.map((abbr) => {
                      const active = selectedStates.includes(abbr);
                      return (
                        <button
                          key={abbr}
                          type="button"
                          onClick={() => toggleState(abbr)}
                          className={`py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap border ${
                            active
                              ? "bg-navy text-white border-navy"
                              : "bg-white text-[#4a5568] border-gray-200 hover:border-navy hover:text-navy"
                          }`}
                        >
                          {abbr}
                        </button>
                      );
                    })}
                  </div>

                  {stateError && (
                    <p className="text-xs text-red-500 mt-2">
                      Please select at least one state.
                    </p>
                  )}

                  {/* Quick-select helpers */}
                  <div className="flex items-center gap-3 mt-2.5">
                    <button
                      type="button"
                      onClick={() => { setSelectedStates([...US_STATES]); setStateError(false); }}
                      className="text-xs text-primary hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Select all
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => setSelectedStates([])}
                      className="text-xs text-[#4a5568] hover:text-navy hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white font-semibold py-3.5 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap mt-1 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    config.title
                  )}
                </button>

                <p className="text-center text-xs text-gray-400 pb-2">
                  No spam. No pressure. Response within 1 business day.
                </p>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
